import { useEffect, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN;

interface UserProfile {
  name: string;
  username: string;
  city: string;
  country: string;
  latitude: number;
  longitude: number;
  avatar?: string;
}

export default function CommunityGlobe() {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const markersRef = useRef<mapboxgl.Marker[]>([]);
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState<UserProfile | null>(null);
  const [clusterView, setClusterView] = useState<UserProfile[] | null>(null);

  // Handle avatar image errors
  const handleAvatarError = (e: React.SyntheticEvent<HTMLImageElement>, name: string) => {
    const target = e.target as HTMLImageElement;
    target.style.display = 'none';
    const parent = target.parentElement;
    if (parent) {
      parent.style.background = 'linear-gradient(135deg, #E67E22, #D35400)';
      parent.innerHTML = `
        <div style="display: flex; align-items: center; justify-content: center; width: 100%; height: 100%; color: white; font-weight: bold;">
          ${name.charAt(0).toUpperCase()}
        </div>
      `;
    }
  };

  // Load user data
  useEffect(() => {
    fetch("https://cbuxrdarzwtolyjhnhzw.supabase.co/storage/v1/object/public/globe-cache/community_map.json")
      .then(res => res.json())
      .then(data => {
        const users = Array.isArray(data) ? data : data.data;
        
        const enriched = users.map((user: UserProfile, index: number) => {
          // Generate avatar with multiple fallbacks
          let avatarSeed = user.username || user.name || user.city || `user-${index}`;
          let avatar = user.avatar;
          
          // If no avatar exists, generate one using DiceBear
          if (!avatar || avatar.trim() === '') {
            avatar = `https://api.dicebear.com/9.x/avataaars/svg?seed=${encodeURIComponent(avatarSeed)}`;
          }
          
          return {
            ...user,
            latitude: Number(user.latitude),
            longitude: Number(user.longitude),
            avatar: avatar
          };
        });
        
        setUsers(enriched);
        setIsLoading(false);
      })
      .catch(err => {
        setIsLoading(false);
      });
  }, []);

  // Initialize map
  useEffect(() => {
    if (!mapContainer.current || map.current || users.length === 0) return;

    mapboxgl.accessToken = MAPBOX_TOKEN || '';

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/dark-v11',
      center: [0, 20],
      zoom: 2,
      projection: { name: 'globe' }
    });

    map.current.on('load', () => {
      if (!map.current) return;

      // Enable globe atmosphere with very subtle orange accent
      map.current.setFog({
        color: 'rgb(45, 35, 30)',
        'high-color': 'rgb(120, 80, 60)',
        'horizon-blend': 0.02,
        'space-color': 'rgb(10, 10, 15)',
        'star-intensity': 0.2
      });

      // Cluster users by location
      const locationMap = new Map<string, UserProfile[]>();
      users.forEach(user => {
        const key = `${user.city},${user.country}`;
        if (!locationMap.has(key)) {
          locationMap.set(key, []);
        }
        locationMap.get(key)!.push(user);
      });

      // Add markers for each location
      locationMap.forEach((locationUsers, key) => {
        const firstUser = locationUsers[0];
        
        // Create marker element
        const el = document.createElement('div');
        el.className = 'community-marker';
        
        if (locationUsers.length > 1) {
          // Cluster marker
          el.innerHTML = `
            <div style="
              width: 44px;
              height: 44px;
              border-radius: 50%;
              background: linear-gradient(135deg, rgba(230, 126, 34, 0.85), rgba(211, 84, 0, 0.85));
              border: 2px solid rgba(255, 255, 255, 0.9);
              display: flex;
              align-items: center;
              justify-content: center;
              font-weight: bold;
              color: white;
              cursor: pointer;
              box-shadow: 0 2px 8px rgba(230, 126, 34, 0.2), 0 0 0 1px rgba(230, 126, 34, 0.1);
              backdrop-filter: blur(4px);
            ">
              ${locationUsers.length}
            </div>
          `;
        } else {
          // Single user marker
          el.innerHTML = `
            <div style="
              width: 36px;
              height: 36px;
              border-radius: 50%;
              border: 2px solid rgba(255, 255, 255, 0.8);
              overflow: hidden;
              cursor: pointer;
              box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(230, 126, 34, 0.15);
              background: linear-gradient(135deg, rgba(230, 126, 34, 0.9), rgba(211, 84, 0, 0.9));
            ">
              <img 
                src="${firstUser.avatar}" 
                alt="${firstUser.name}" 
                style="width: 100%; height: 100%; object-fit: cover;"
                onerror="this.style.display='none'; this.parentElement.innerHTML='<div style=\\'display:flex;align-items:center;justify-content:center;width:100%;height:100%;color:white;font-weight:bold;font-size:13px;\\'>${firstUser.name.charAt(0).toUpperCase()}</div>';"
              />
            </div>
          `;
        }

        // Create popup content
        let popupContent = '';
        if (locationUsers.length > 1) {
          popupContent = `
            <div style="font-family: 'Inter', sans-serif; min-width: 200px;">
              <div style="font-weight: bold; color: #E67E22; font-size: 16px; margin-bottom: 8px;">
                📍 ${firstUser.city}, ${firstUser.country}
              </div>
              <div style="font-size: 14px; margin-bottom: 8px;">
                <span style="background: #E67E22; padding: 4px 8px; border-radius: 4px; color: white; font-weight: bold;">
                  ${locationUsers.length} members
                </span>
              </div>
              <div style="font-size: 12px; opacity: 0.7; font-style: italic;">
                Click marker to view all members
              </div>
            </div>
          `;
        } else {
          popupContent = `
            <div style="font-family: 'Inter', sans-serif; min-width: 180px;">
              <div style="display: flex; align-items: center; margin-bottom: 8px;">
                <img 
                  src="${firstUser.avatar}" 
                  style="width: 40px; height: 40px; border-radius: 50%; margin-right: 10px; border: 2px solid #E67E22; object-fit: cover;"
                  onerror="this.style.display='none';"
                />
                <div>
                  <div style="font-weight: bold; color: #E67E22;">${firstUser.name}</div>
                  <div style="font-size: 12px; opacity: 0.8;">@${firstUser.username}</div>
                </div>
              </div>
              <div style="font-size: 12px;">📍 ${firstUser.city}, ${firstUser.country}</div>
            </div>
          `;
        }

        const popup = new mapboxgl.Popup({ 
          offset: 25,
          closeButton: false,
          className: 'custom-popup'
        }).setHTML(popupContent);

        const marker = new mapboxgl.Marker(el)
          .setLngLat([firstUser.longitude, firstUser.latitude])
          .setPopup(popup)
          .addTo(map.current!);

        // Handle click
        el.addEventListener('click', () => {
          if (locationUsers.length > 1) {
            setClusterView(locationUsers);
          }
          // Individual user clicks do nothing (profile card removed)
        });

        markersRef.current.push(marker);
      });
    });

    return () => {
      markersRef.current.forEach(marker => marker.remove());
      markersRef.current = [];
      map.current?.remove();
      map.current = null;
    };
  }, [users]);

  if (isLoading) {
    return (
      <div 
        className="h-full w-full p-6 flex items-center justify-center"
        style={{
          background: "linear-gradient(135deg, #0a0a0f 0%, #1a0a0a 50%, #0a0a0f 100%)",
        }}
      >
        <div className="text-center">
          <div 
            className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 mx-auto mb-4"
            style={{ borderColor: "#E67E22" }}
          />
          <p className="text-white text-sm" style={{ fontFamily: "'Bensoon', sans-serif" }}>
            Loading community...
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Custom Mapbox Styling */}
      <style>{`
        .mapboxgl-ctrl-bottom-right,
        .mapboxgl-ctrl-logo {
          display: none !important;
        }

        .mapbox-community-map .mapboxgl-canvas {
          border-radius: 16px;
        }
        
        .mapboxgl-ctrl-attrib {
          background: rgba(0, 0, 0, 0.8) !important;
          color: rgba(255, 255, 255, 0.6) !important;
          padding: 4px 8px !important;
          border-radius: 4px !important;
        }

        .mapboxgl-popup-content {
          background: rgba(0, 0, 0, 0.95) !important;
          border: 1px solid #E67E22 !important;
          border-radius: 8px !important;
          padding: 12px !important;
          color: white !important;
        }

        .mapboxgl-popup-tip {
          border-top-color: rgba(0, 0, 0, 0.95) !important;
        }

        /* Marker hover animation */
        .community-marker > div {
          transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
        }

        .community-marker:hover > div {
          transform: scale(1.1);
          box-shadow: 0 4px 12px rgba(230, 126, 34, 0.3), 0 0 0 2px rgba(230, 126, 34, 0.2) !important;
          border-color: rgba(230, 126, 34, 0.9) !important;
        }
      `}</style>

      {/* Dark Background Container */}
      <div 
        className="h-full w-full p-6 relative"
        style={{
          background: "linear-gradient(135deg, #0a0a0f 0%, #1a0a0a 50%, #0a0a0f 100%)",
        }}
      >
        {/* Map Container with Subtle Border */}
        <div 
          className="h-full w-full relative mapbox-community-map"
          style={{
            padding: "1px",
            background: "linear-gradient(135deg, rgba(230, 126, 34, 0.4) 0%, rgba(243, 156, 18, 0.3) 50%, rgba(230, 126, 34, 0.4) 100%)",
            borderRadius: "18px",
            boxShadow: "0 0 30px rgba(230, 126, 34, 0.15)",
          }}
        >
          <div 
            ref={mapContainer}
            className="h-full w-full"
            style={{
              background: "#0a0a0f",
              borderRadius: "16px",
            }}
          />
        </div>

        {/* Stats Overlay */}
        <div 
          className="absolute top-10 left-10 right-10 z-10 text-center"
          style={{ pointerEvents: 'none' }}
        >
          <h2 
            className="text-2xl md:text-3xl font-bold text-white mb-3"
            style={{ 
              fontFamily: "'Bensoon', sans-serif",
              textShadow: "0 2px 10px rgba(0, 0, 0, 0.8)" 
            }}
          >
            <span style={{ color: "#E67E22" }}>{users.length}</span> Community Members
          </h2>
          <p 
            className="text-sm text-white/70"
            style={{ fontFamily: "'Bensoon', sans-serif" }}
          >
            Click markers to view profiles
          </p>
        </div>
      </div>

      {/* Cluster View Modal */}
      {clusterView && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ 
            background: "rgba(0, 0, 0, 0.8)",
            backdropFilter: "blur(10px)",
          }}
          onClick={() => setClusterView(null)}
        >
          <div 
            className="relative rounded-xl p-6 text-white shadow-2xl w-full max-w-3xl max-h-[80vh] overflow-auto"
            style={{
              background: "linear-gradient(135deg, rgba(20, 20, 25, 0.98) 0%, rgba(40, 40, 50, 0.98) 100%)",
              border: "2px solid #E67E22",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 
                  className="text-2xl font-bold mb-2"
                  style={{ fontFamily: "'Bensoon', sans-serif", color: "#E67E22" }}
                >
                  📍 {clusterView[0]?.city}, {clusterView[0]?.country}
                </h2>
                <p 
                  className="text-sm opacity-70"
                  style={{ fontFamily: "'Bensoon', sans-serif" }}
                >
                  {clusterView.length} members in this location
                </p>
              </div>
              <button
                onClick={() => setClusterView(null)}
                className="text-white/60 hover:text-white transition-colors"
                style={{ 
                  fontSize: "32px",
                  lineHeight: "1",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                ×
              </button>
            </div>

            {/* Grid of Members */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {clusterView.map((user, index) => (
                <div 
                  key={`${user.username}-${index}`}
                  className="flex items-center p-4 rounded-lg hover:bg-white/5 transition-colors"
                  style={{ border: "1px solid rgba(230, 126, 34, 0.3)" }}
                >
                  <div
                    className="w-12 h-12 rounded-full mr-4 flex-shrink-0 overflow-hidden"
                    style={{ border: "2px solid #E67E22", background: "linear-gradient(135deg, #E67E22, #D35400)" }}
                  >
                    <img
                      src={user.avatar}
                      alt={user.name}
                      className="w-full h-full object-cover"
                      onError={(e) => handleAvatarError(e, user.name)}
                    />
                  </div>
                  <div>
                    <div 
                      className="font-semibold"
                      style={{ fontFamily: "'Bensoon', sans-serif" }}
                    >
                      {user.name}
                    </div>
                    <div 
                      className="text-sm"
                      style={{ 
                        color: "#E67E22",
                        fontFamily: "'Bensoon', sans-serif" 
                      }}
                    >
                      @{user.username}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

    </>
  );
}